import {
  AppModuleCode,
  PermissionCode,
  TicketSlaRisk,
  type AuthUser,
  type DashboardHelpdeskSummary,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';

import { berlinDateParts } from '@/products/helpdesk/features/changes/change-upcoming';
import {
  DashboardPanel,
  DashboardPanelLinkAction,
} from '@/products/helpdesk/features/dashboard/dashboard-panel';
import { DashboardMiniBars } from '@/products/helpdesk/features/dashboard/dashboard-mini-bars';
import { useDashboardSummary } from '@/products/helpdesk/features/dashboard/use-dashboard-summary';
import { hasPermission, isModuleEnabled } from '@/features/auth/permissions';
import { OPEN_TICKET_STATUSES } from '@/products/helpdesk/features/tickets/home-ticket-queue';
import { ticketListPath } from '@/products/helpdesk/features/tickets/ticket-list-path';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

function berlinTodayYmd(): string {
  const { year, month, day } = berlinDateParts();
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function HelpdeskDashboardPanel({ user }: { user: AuthUser }) {
  const canTeam = hasPermission(user, PermissionCode.TICKETS_READ_ALL);
  const queuesOn = isModuleEnabled(user, AppModuleCode.QUEUES);
  const slaOn = isModuleEnabled(user, AppModuleCode.SLA);
  const summary = useDashboardSummary({ enabled: canTeam });

  return (
    <DashboardPanel
      slotId="helpdesk"
      title="Tickets / Helpdesk"
      description="Volumen, SLA und Tagesverlauf (Europe/Berlin)."
      action={<DashboardPanelLinkAction to="/helpdesk/tickets" label="Tickets" />}
    >
      {!canTeam ? (
        <p className="text-sm text-muted-foreground">
          Team-Kennzahlen sind nur mit der Berechtigung zur Gesamtsicht verfügbar.
        </p>
      ) : null}

      {canTeam && summary.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Kennzahlen werden geladen …
        </p>
      ) : null}

      {canTeam && summary.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {summary.error instanceof ApiRequestError
            ? summary.error.message
            : 'Das Helpdesk-Lagebild konnte nicht geladen werden.'}
        </p>
      ) : null}

      {canTeam && summary.data?.helpdesk ? (
        <HelpdeskBody helpdesk={summary.data.helpdesk} queuesOn={queuesOn} slaOn={slaOn} />
      ) : null}

      {canTeam && summary.data && !summary.data.helpdesk && !summary.isPending ? (
        <p className="text-sm text-muted-foreground">Keine Helpdesk-Daten verfügbar.</p>
      ) : null}
    </DashboardPanel>
  );
}

function HelpdeskBody({
  helpdesk,
  queuesOn,
  slaOn,
}: {
  helpdesk: DashboardHelpdeskSummary;
  queuesOn: boolean;
  slaOn: boolean;
}) {
  const { kpis, seriesDaily, openByPriority, topQueues } = helpdesk;

  const today = berlinTodayYmd();

  return (
    <div className="space-y-4">
      <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-3">
        <Kpi
          label="Offen"
          value={kpis.openTotal}
          href={ticketListPath({ status: [...OPEN_TICKET_STATUSES] })}
        />
        <Kpi
          label="Neu heute"
          value={kpis.createdToday}
          href={ticketListPath({ createdFrom: today, createdTo: today })}
        />
        <Kpi
          label="Gelöst/geschl. heute"
          value={kpis.resolvedOrClosedToday}
          href="/helpdesk/tickets"
        />
        <Kpi
          label="Unzugewiesen"
          value={kpis.unassignedOpen}
          href={ticketListPath({
            unassigned: true,
            status: [...OPEN_TICKET_STATUSES],
          })}
        />
        {slaOn && kpis.slaAtRisk !== null ? (
          <Kpi
            label="SLA-Risiko"
            value={kpis.slaAtRisk}
            href={ticketListPath({
              slaRisk: TicketSlaRisk.AT_RISK,
              status: [...OPEN_TICKET_STATUSES],
              sortBy: 'slaDueAt',
              sortOrder: 'asc',
            })}
            tone={kpis.slaAtRisk > 0 ? 'warn' : 'default'}
          />
        ) : null}
        {kpis.majorIncidentsOpen !== null ? (
          <Kpi
            label="Major offen"
            value={kpis.majorIncidentsOpen}
            href={ticketListPath({
              isMajor: true,
              status: [...OPEN_TICKET_STATUSES],
            })}
            tone={kpis.majorIncidentsOpen > 0 ? 'warn' : 'default'}
          />
        ) : null}
      </div>

      <DashboardMiniBars series={seriesDaily} />

      {openByPriority.length > 0 ? (
        <div>
          <p className="mb-1.5 text-xs font-medium tracking-wide uppercase">Offen nach Priorität</p>
          <ul className="space-y-1">
            {openByPriority.map((row) => (
              <li key={row.priority} className="flex items-center justify-between gap-2 text-sm">
                <Link
                  to={ticketListPath({
                    priority: [row.priority],
                    status: [...OPEN_TICKET_STATUSES],
                  })}
                  className="truncate underline-offset-2 hover:text-primary hover:underline"
                >
                  {row.priority}
                </Link>
                <span className="tabular-nums">{row.count}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {queuesOn && topQueues.length > 0 ? (
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <p className="text-xs font-medium tracking-wide uppercase">Top-Warteschlangen</p>
            <Link
              to="/helpdesk/warteschlangen"
              className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              Board
            </Link>
          </div>
          <ul className="space-y-1">
            {topQueues.map((row) => (
              <li key={row.queueId} className="flex items-center justify-between gap-2 text-sm">
                <Link
                  to={ticketListPath({
                    queueId: [row.queueId],
                    status: [...OPEN_TICKET_STATUSES],
                  })}
                  className="truncate underline-offset-2 hover:text-primary hover:underline"
                >
                  {row.name}
                </Link>
                <span className="tabular-nums">{row.count}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function Kpi({
  label,
  value,
  href,
  tone = 'default',
}: {
  label: string;
  value: number;
  href: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <Link
      to={href}
      className={cn(
        'min-w-0 rounded-md border px-2.5 py-2 transition-colors hover:bg-muted/60',
        tone === 'warn' && 'border-amber-300/80 bg-amber-50/80 dark:bg-amber-950/30',
      )}
    >
      <p className="text-[11px] leading-tight break-words text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tracking-tight tabular-nums">{value}</p>
    </Link>
  );
}
